// The loaded-data meter of a live cluster, and the worst case of every
// transaction shape whose `loadedAccountsDataSizeLimit` is fixed before the
// one-time key signs (R70, docs/reviews/2026-10-06-deployment-upgrade.md).
//
// The app signs an Execute before it simulates it (R11): a transaction the
// cluster then refuses for its loaded-data limit has already exposed a leaf,
// and that leaf is burned. So a limit chosen before signing must hold for
// whatever the transaction's accounts can hold when it lands, not for what
// they held when it was built. Pure arithmetic; no network.
//
// The meter (SIMD-0186, as a live cluster applies it): every account key that
// exists costs 64 bytes plus its data; a key that does not exist yet costs
// nothing; a key owned by LoaderV3 in its Program state also costs its
// ProgramData account, 64 bytes plus its data. litesvm and the local shim
// leave the ProgramData out, so only a live cluster shows it.
// `loadedAccountsDataSize` matches six saved live figures, four of them as
// checks (test/loaded-data.test.ts).

import { VAULT_LEN } from './recovery.js';
import { MAX_LOADED_ACCOUNTS_DATA_SIZE, PLAIN_LOADED_ACCOUNTS_DATA_SIZE } from './v1.js';

/** What the meter adds per loaded account on top of its data. */
export const LOADED_ACCOUNT_BASE_SIZE = 64;
/** The runtime's cap on one account's data (`MAX_PERMITTED_DATA_LENGTH`, 10 MiB): System `Allocate` and LoaderV3 `ExtendProgram` both stop there. */
export const MAX_ACCOUNT_DATA_LEN = 10 * 1024 * 1024;
/** The smallest LoaderV3 Program account, and the size the CLI gives one: a 4-byte state tag and the 32-byte ProgramData address. The loader refuses a smaller one at deploy and never resizes it afterwards. */
export const LOADER_V3_PROGRAM_LEN = 36;
/** A LoaderV3 ProgramData account's header before the ELF region: tag, deploy slot, authority option. */
export const LOADER_V3_PROGRAMDATA_HEADER_LEN = 45;
/**
 * The SPL Token program's ProgramData account, 108,645 bytes, and the ATA
 * program's account (BPF Loader 2), 105,032 bytes, as the pinned mainnet
 * snapshot records them (sdk/fixtures/jupiter/sol-usdc-route-raydium-clmm.json,
 * slot 447,789,991). Neither can change: SPL Token's upgrade authority is
 * `null`, and LoaderV3 refuses to extend a program without one; a BPF Loader 2
 * program is immutable.
 */
export const SPL_TOKEN_PROGRAMDATA_LEN = 108_645;
export const ATA_PROGRAM_LEN = 105_032;
/**
 * An initialized System nonce account's data, 80 bytes (`State::size()`,
 * `solana-nonce` 3.2.1 `src/state.rs:101-102`). Besides a System account
 * with no data, it is the only account the cluster accepts as a fee payer
 * (`get_system_account_kind`, `solana-nonce-account` 4.1.1
 * `src/lib.rs:60-85`; Agave v4.3.0 `svm/src/account_loader.rs` L354-L364,
 * cited by the R73 review).
 */
export const NONCE_STATE_LEN = 80;

/** One account key as the meter sees it. `programDataLen` is set for a LoaderV3 Program-state key: its ProgramData account's data length. */
export interface MeteredAccount {
  dataLen: number;
  programDataLen?: number;
}

/**
 * The live meter over a transaction's account keys, each listed once
 * (`null` for a key with no account yet). A ProgramData that is itself one
 * of the keys is counted once by the cluster: list it once.
 */
export function loadedAccountsDataSize(keys: readonly (MeteredAccount | null)[]): number {
  let total = 0;
  for (const key of keys) {
    if (key === null) continue;
    for (const len of key.programDataLen === undefined ? [key.dataLen] : [key.dataLen, key.programDataLen]) {
      if (!Number.isInteger(len) || len < 0 || len > MAX_ACCOUNT_DATA_LEN) throw new RangeError(`account data length ${len} is outside 0..${MAX_ACCOUNT_DATA_LEN}`);
      total += LOADED_ACCOUNT_BASE_SIZE + len;
    }
  }
  return total;
}

/**
 * The most the meter can charge for one key, by what is known about it.
 *
 * - `relayer`: the fee payer when it is the app's own generated gas-tank
 *   key. Only its own key can give a System account data, and the app's key
 *   never does: no data. This is an assumption about that key, not a
 *   property of every fee payer the cluster accepts.
 * - `externalPayer`: a fee payer the caller supplies (the canary's and the
 *   devnet lifecycle's keypair files, or any payer passed to `compileV1`).
 *   Nothing here checks that it holds no data, and the cluster also accepts
 *   an initialized System nonce account as a fee payer: at most
 *   `NONCE_STATE_LEN` (80) bytes.
 * - `vault`: the vault PDA, `VAULT_LEN` (128) bytes. The program never
 *   reallocates it, and before Initialize nobody but the program can sign
 *   for the address, so it holds no data (a deposit that arrived first leaves
 *   a System account with none).
 * - `upgradeableProgram`: a fixed LoaderV3 program id whose Program account
 *   is 36 bytes (the vault program: docs/evidence/devnet/2026-09-28-fee-side/
 *   provenance.json `space` 36; Token-2022: the pinned snapshot's 36-byte
 *   account). Its ProgramData reaches the 10 MiB cap by `ExtendProgram`,
 *   which anyone may call while the program is upgradeable, without changing
 *   a byte of the ELF the app's hash gate checks.
 * - `notLoaderV3`: a key whose owner is not LoaderV3, so the meter adds no
 *   ProgramData: a builtin such as the System program, or a token mint or
 *   token account, which its token program owns. At most the 10 MiB cap.
 * - `arbitrary`: an address the user chooses, such as a withdrawal's
 *   recipient. It may even be a LoaderV3 Program account, which the loader
 *   accepts at any size from 36 bytes up to the cap, with a ProgramData of
 *   up to the cap: twice (64 + 10 MiB).
 * - `splTokenProgram`, `ataProgram`: immutable programs of known size
 *   (`SPL_TOKEN_PROGRAMDATA_LEN`, `ATA_PROGRAM_LEN`).
 */
export type KeyBound = 'relayer' | 'externalPayer' | 'vault' | 'upgradeableProgram' | 'notLoaderV3' | 'arbitrary' | 'splTokenProgram' | 'ataProgram';

export function keyWorstCase(bound: KeyBound): number {
  const B = LOADED_ACCOUNT_BASE_SIZE;
  switch (bound) {
    case 'relayer':
      return B;
    case 'externalPayer':
      return B + NONCE_STATE_LEN;
    case 'vault':
      return B + VAULT_LEN;
    case 'upgradeableProgram':
      return B + LOADER_V3_PROGRAM_LEN + B + MAX_ACCOUNT_DATA_LEN;
    case 'notLoaderV3':
      return B + MAX_ACCOUNT_DATA_LEN;
    case 'arbitrary':
      return 2 * (B + MAX_ACCOUNT_DATA_LEN);
    case 'splTokenProgram':
      return B + LOADER_V3_PROGRAM_LEN + B + SPL_TOKEN_PROGRAMDATA_LEN;
    case 'ataProgram':
      return B + ATA_PROGRAM_LEN;
  }
}

/** 20,971,648 bytes: the most an arbitrary key can cost. Four of them (83,886,592 B) already exceed the 64 MiB maximum, so a shape fits only by the bounds of its keys' roles. */
export const ARBITRARY_KEY_WORST_CASE = keyWorstCase('arbitrary');
/** 10,485,924 bytes: a fixed upgradeable program, the vault program among them. */
export const UPGRADEABLE_PROGRAM_WORST_CASE = keyWorstCase('upgradeableProgram');

/** The most the meter can charge for a transaction whose keys have these bounds. */
export function worstCaseLoadedAccountsDataSize(keys: readonly KeyBound[]): number {
  return keys.reduce((sum, bound) => sum + keyWorstCase(bound), 0);
}

export interface LoadedDataShape {
  name: string;
  /** Where the clients build it. */
  builtBy: string;
  /** The `loadedAccountsDataSizeLimit` its builders request. */
  limit: number;
  /** Its account keys after `compileV1`'s de-duplication, each with its bound. */
  keys: readonly { role: string; bound: KeyBound }[];
}

const relayer = { role: 'relayer (fee payer)', bound: 'relayer' } as const;
const vault = { role: 'vault', bound: 'vault' } as const;
const vaultProgram = { role: 'vault program (with its ProgramData)', bound: 'upgradeableProgram' } as const;
const systemProgram = { role: 'System program', bound: 'notLoaderV3' } as const;
const recipient = { role: 'recipient', bound: 'arbitrary' } as const;
const splToken = { role: 'SPL Token program', bound: 'splTokenProgram' } as const;
const ataProgram = { role: 'ATA program', bound: 'ataProgram' } as const;

/**
 * Every plain transaction shape: the ones that request
 * `PLAIN_LOADED_ACCOUNTS_DATA_SIZE`, with the fee payer the app supports,
 * its own generated key, which holds no data (`relayer`). Each fits the
 * 64 MiB maximum at its worst by its keys' roles (at most 31,457,828 bytes,
 * an Execute with an arbitrary recipient), so no plain shape needs a check
 * before its key is reserved. That figure is exact for an empty payer only;
 * a caller that supplies its own payer, the scripts included, gets
 * `PLAIN_EXTERNAL_PAYER_WORST_CASE` (80 bytes more for a nonce-account
 * payer). Both also fit 32 MiB: the maximum is the selected conservative
 * policy (R70, R73), not the smallest limit these shapes need. The argument
 * needs the roles: with four arbitrary keys a shape could exceed the maximum.
 */
export const PLAIN_SHAPES: readonly LoadedDataShape[] = [
  {
    name: 'Initialize',
    builtBy: 'app Activate (engine.ts activate); mainnet canary treasury and vault initialisation; devnet lifecycle',
    limit: PLAIN_LOADED_ACCOUNTS_DATA_SIZE,
    keys: [relayer, vault, systemProgram, vaultProgram],
  },
  {
    name: 'System transfer from the relayer',
    builtBy: "app gas-tank send-out (engine.ts sendGas); canary deposit; devnet lifecycle fund, top-up and smoke transfer",
    limit: PLAIN_LOADED_ACCOUNTS_DATA_SIZE,
    keys: [relayer, recipient, systemProgram],
  },
  {
    name: 'Execute with one aux account (LAMPORT_MOVE)',
    builtBy: 'app native withdrawal (engine.ts withdrawNative); devnet lifecycle transfer, retry and rotation',
    limit: PLAIN_LOADED_ACCOUNTS_DATA_SIZE,
    keys: [relayer, vault, recipient, vaultProgram],
  },
  {
    name: 'Execute without aux accounts (recovery message)',
    builtBy: 'app key refresh (engine.ts refreshKey); canary recovery; devnet lifecycle recovery and Reopen',
    limit: PLAIN_LOADED_ACCOUNTS_DATA_SIZE,
    keys: [relayer, vault, vaultProgram],
  },
  {
    name: 'Close',
    builtBy: 'devnet lifecycle close (instructions.ts closeInstruction)',
    limit: PLAIN_LOADED_ACCOUNTS_DATA_SIZE,
    keys: [relayer, vault, recipient, vaultProgram],
  },
];

/**
 * The token-touching shapes, which already requested the maximum before R70
 * (app `TOKEN_TOUCHING_LIMITS`, canary `TOKEN_TOUCHING_LIMITS`). The wrap and
 * the unwrap fit it by their keys' roles. A token withdrawal's bound is
 * loose: with the System program's account and the two token accounts each
 * counted at the 10 MiB cap it comes to 83,992,144 bytes, over the maximum;
 * it fits whenever those three hold at most `tokenWithdrawalSlack()` bytes of
 * data together, which nothing here proves. Swaps (`LADDER_LIMITS`) carry
 * router-chosen accounts and have no bound here. For those two, no larger
 * limit exists; only the simulation, after the key has signed, shows
 * whether they fit. The figures use the app's empty generated payer
 * (`relayer`); the canary's wrap, whose payer is a keypair file the caller
 * supplies, is at most `NONCE_STATE_LEN` more (`withExternalPayer`:
 * 42,157,637 bytes), still within the maximum (R73).
 */
export const TOKEN_SHAPES: readonly LoadedDataShape[] = [
  {
    name: 'Wrap (make swappable)',
    builtBy: 'app wrapStray; canary wrap (canary.ts wrapTransactionInstructions)',
    limit: MAX_LOADED_ACCOUNTS_DATA_SIZE,
    keys: [relayer, vault, { role: "vault's wSOL account", bound: 'notLoaderV3' }, { role: 'wSOL mint', bound: 'notLoaderV3' }, systemProgram, splToken, ataProgram, vaultProgram],
  },
  {
    name: 'Unwrap',
    builtBy: 'app unwrap',
    limit: MAX_LOADED_ACCOUNTS_DATA_SIZE,
    keys: [relayer, vault, { role: "vault's wSOL account", bound: 'notLoaderV3' }, splToken, vaultProgram],
  },
  {
    name: 'Token withdrawal',
    builtBy: 'app withdrawToken',
    limit: MAX_LOADED_ACCOUNTS_DATA_SIZE,
    keys: [
      relayer,
      vault,
      { role: "vault's token account", bound: 'notLoaderV3' },
      { role: 'mint', bound: 'notLoaderV3' },
      { role: "recipient's token account", bound: 'notLoaderV3' },
      recipient,
      // SPL Token or Token-2022; Token-2022 is the larger bound.
      { role: 'token program', bound: 'upgradeableProgram' },
      ataProgram,
      systemProgram,
      vaultProgram,
    ],
  },
];

/** A shape's worst case under its keys' bounds. */
export function shapeWorstCase(shape: LoadedDataShape): number {
  return worstCaseLoadedAccountsDataSize(shape.keys.map((k) => k.bound));
}

/**
 * The same shape with a fee payer the caller supplies (`externalPayer`)
 * in place of the app's empty generated key: the general SDK caller
 * contract, as against the app's supported shapes.
 */
export function withExternalPayer(shape: LoadedDataShape): LoadedDataShape {
  return { ...shape, keys: shape.keys.map((k) => (k.bound === 'relayer' ? { role: k.role, bound: 'externalPayer' as const } : k)) };
}

/**
 * 31,457,908 bytes: the largest plain shape's worst case with a fee payer the
 * caller supplies, which may be an initialized System nonce account (80 B of
 * data); the empty-payer figure, 31,457,828 B, plus `NONCE_STATE_LEN`.
 * (Marked pure so the app's bundle, which never reads it, drops it.)
 */
export const PLAIN_EXTERNAL_PAYER_WORST_CASE = /* @__PURE__ */ largestPlainWithExternalPayer();

function largestPlainWithExternalPayer(): number {
  return Math.max(...PLAIN_SHAPES.map((s) => shapeWorstCase(withExternalPayer(s))));
}

/** The token-withdrawal roles whose cap bound is loose: the System program and the two token accounts. */
export const TOKEN_WITHDRAWAL_LOOSE_ROLES: readonly string[] = ['System program', "vault's token account", "recipient's token account"];

/**
 * How many bytes of data the System program's account and the two token
 * accounts of a token withdrawal may hold together, with every other key at
 * its worst, before the transaction exceeds the 64 MiB maximum.
 */
export function tokenWithdrawalSlack(): number {
  const shape = TOKEN_SHAPES.find((s) => s.name === 'Token withdrawal')!;
  const rest = shape.keys.filter((k) => !TOKEN_WITHDRAWAL_LOOSE_ROLES.includes(k.role));
  return shape.limit - worstCaseLoadedAccountsDataSize(rest.map((k) => k.bound)) - LOADED_ACCOUNT_BASE_SIZE * TOKEN_WITHDRAWAL_LOOSE_ROLES.length;
}

/**
 * The meter's charge for the plain shapes that load the vault program,
 * leaving out the ProgramData's own data, with the small accounts they
 * carry in normal use: an empty relayer (the app's generated key; a
 * nonce-account payer would take 80 bytes more), the 128-byte vault, a recipient
 * with no data, the 36-byte Program account. The native withdrawal (and
 * Close) is the largest, 484 bytes; Initialize (64 + 0 + System + 100 + 64)
 * measured 313 on mainnet and 306 on devnet (test/loaded-data.test.ts).
 */
export const PLAIN_SMALL_ACCOUNT_OVERHEAD = loadedAccountsDataSize([{ dataLen: 0 }, { dataLen: VAULT_LEN }, { dataLen: 0 }, { dataLen: LOADER_V3_PROGRAM_LEN, programDataLen: 0 }]);

/**
 * The largest ProgramData account (header plus ELF region, in bytes) for
 * which every plain shape with small accounts still fits `limit` (R71): the
 * size criterion for an extension while builds with that plain limit may
 * still be open.
 */
export function largestProgramDataFor(limit: number): number {
  return limit - PLAIN_SMALL_ACCOUNT_OVERHEAD;
}
